import { createElement, useCallback, useEffect, useMemo, useState } from 'react';
import type { ComponentType, ReactElement } from 'react';
import type { ScreenAction } from '@taki/ui';
import type { ActionDeFicheAssemblee } from './assemblage.js';
import type { FicheGenerique, ProprietesDeFenetreDeFiche } from './app-declaree.js';

/**
 * LES GESTES DE NIVEAU FICHE QUE LA DECLARATION POSE (`niveau: "fiche"`, chantier
 * `actions-fiche-sans-plafond`, `docs/socle/actions-fiche-sans-plafond.md`).
 *
 * UN GESTE, UNE DECLARATION, TOUTES LES VUES DE LA FICHE : la rangee 3 de la
 * fiche ouverte les rend a gauche apres la sortie, sur la Fiche standard comme
 * sur une vue Canvas (qui pose les siens avant, `actions-de-vue-de-fiche.ts`).
 * Jamais sur un brouillon : `ficheOuverte` est alors nulle, et il n'y a pas
 * encore de fiche a qui donner le geste.
 *
 * SON EFFET est un `fn` d'app, appele avec le contexte habituel (`ficheId` = la
 * fiche ouverte), ou une FENETRE d'app (`fenetres` de `definirAppDeclaree`) :
 * un composant React ordinaire, monte ici a cote du gabarit, qui rend son propre
 * `Modal`. Un effet ne peut ni ouvrir de fenetre ni utiliser un hook ; la
 * fenetre, si.
 *
 * LA FENETRE SE DEMONTE d'elle-meme quand la fiche ou la porte change : elle est
 * l'affaire de CETTE fiche, pas de la suivante.
 */

interface FenetreOuverte {
  action: string;
  fiche: string;
  porte: string;
}

export interface OptionsDesActionsDeFiche {
  /** Les gestes de niveau fiche de la porte active. */
  actions: readonly ActionDeFicheAssemblee[];
  /** Les fenetres de l'app, par leur nom. */
  fenetres: Readonly<Record<string, ComponentType<ProprietesDeFenetreDeFiche>>>;
  /** L'id de la porte active. */
  porte: string;
  /** L'id de la fiche ouverte, `null` sur un brouillon, un modele ou sans fiche. */
  ficheOuverte: string | null;
  /** La fiche ouverte, brute, `undefined` tant qu'elle n'est pas lue. */
  fiche: FicheGenerique | undefined;
  /** La raison pour laquelle la porte ne s'ecrit pas (`useScreen().raisonAcces`) : les gestes s'eteignent avec elle (G16). */
  raisonAcces: string | null;
}

/**
 * Les entrees de la rangee 3 de la fiche, et la fenetre ouverte (a rendre en
 * frere du gabarit), `null` quand aucune ne l'est.
 */
export function useActionsDeFicheDeclarees(options: OptionsDesActionsDeFiche): {
  rangee: ScreenAction[];
  fenetre: ReactElement | null;
} {
  const { actions, fenetres, porte, ficheOuverte, fiche, raisonAcces } = options;
  const [ouverte, setOuverte] = useState<FenetreOuverte | null>(null);
  const valable = ouverte !== null && ouverte.fiche === ficheOuverte && ouverte.porte === porte;
  /* LA FENETRE DE LA FICHE PRECEDENTE NE REVIENT PAS avec elle : l'etat tombe,
     il ne se contente pas de ne plus se rendre. */
  useEffect(() => {
    if (ouverte !== null && !valable) setOuverte(null);
  }, [ouverte, valable]);
  const fermer = useCallback(() => setOuverte(null), []);

  const rangee = useMemo<ScreenAction[]>(() => {
    if (ficheOuverte === null) return [];
    return actions.map((action): ScreenAction => {
      /* UNE FENETRE QUE L'APP NE FOURNIT PAS EST UN REFUS, comme un effet absent
         (`actionsDe`) : le montage de l'app l'a deja tenu pour sa propre
         declaration, ceci attrape celle qu'une org a ecrite. Un bouton muet
         serait un bouton qui ment. */
      if (action.fenetre !== undefined && fenetres[action.fenetre] === undefined) {
        throw new Error(`L'action "${action.id}" nomme la fenetre "${action.fenetre}", que l'app ne fournit pas.`);
      }
      const entree: ScreenAction = {
        id: action.id,
        label: action.label,
        ...(action.variant === undefined ? {} : { variant: action.variant }),
        disabled: raisonAcces !== null,
        ...(raisonAcces === null ? {} : { disabledReason: raisonAcces }),
        onClick: () => {
          if (action.fenetre !== undefined) setOuverte({ action: action.id, fiche: ficheOuverte, porte });
          else action.lancer?.();
        },
      };
      return entree;
    });
  }, [actions, fenetres, ficheOuverte, porte, raisonAcces]);

  const courante = valable ? actions.find((action) => action.id === ouverte.action) : undefined;
  const Fenetre = courante?.fenetre === undefined ? undefined : fenetres[courante.fenetre];
  const fenetre =
    Fenetre === undefined || fiche === undefined || ouverte === null
      ? null
      : createElement(Fenetre, { key: `${ouverte.action}:${ouverte.fiche}`, fiche, porte, fermer });
  return { rangee, fenetre };
}
